import axios, { AxiosProgressEvent } from 'axios';
import {
  CreatePostRequest,
  CreatorPostsPage,
  UploadUrlRequest,
  UploadUrlTarget,
} from '../types/post';
import { parseUploadUrlResponse, toPercent } from '../util/postPublish';
import { parsePostsPage } from '../util/postTimeline';
import { authHeader } from './authHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const postService = {
  // Step 1: ask the backend where to upload the image.
  async requestUploadUrl(req: UploadUrlRequest): Promise<{
    success: boolean;
    message: string;
    target?: UploadUrlTarget;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      const response = await axios.post(`${API_URL}/api/posts/upload-url`, req, { headers });

      const parsed = parseUploadUrlResponse(response.data);
      if (!parsed.ok) {
        return { success: false, message: parsed.reason };
      }

      return {
        success: true,
        message: '',
        target: {
          postId: parsed.postId,
          path: parsed.path,
          uploadUrl: parsed.uploadUrl,
          contentType: response.data?.contentType ?? req.contentType,
          expiresAt: response.data?.expiresAt ?? '',
        },
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to start the upload',
      };
    }
  },

  // Step 2: PUT the raw file to the signed URL. The signature is the credential,
  // so this request must not carry an Authorization header.
  async uploadImage(
    target: UploadUrlTarget,
    file: File,
    onProgress: (percent: number) => void,
  ): Promise<{ success: boolean; message: string }> {
    try {
      await axios.put(target.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (event: AxiosProgressEvent) => {
          onProgress(toPercent(event.loaded, event.total));
        },
      });
      return { success: true, message: '' };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to upload the image',
      };
    }
  },

  // Step 3: create the post that points at the uploaded object.
  async createPost(req: CreatePostRequest): Promise<{
    success: boolean;
    message: string;
    postId?: string;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      const response = await axios.post(`${API_URL}/api/posts`, req, { headers });

      return { success: true, message: 'Post published', postId: response.data?.id };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to create the post',
      };
    }
  },

  // One page of a creator's posts, newest first. The backend decides per request
  // which of them are locked, so a viewer who just subscribed has to re-read
  // page 1 to see the images. No `limit` is sent: the backend's default and its
  // cap are both 20.
  async listCreatorPosts(
    creatorId: string,
    cursor?: string | null,
  ): Promise<{
    success: boolean;
    message: string;
    page?: CreatorPostsPage;
  }> {
    try {
      const headers = await authHeader();
      if (!headers) {
        return { success: false, message: 'Please log in again.' };
      }

      const response = await axios.get(
        `${API_URL}/api/posts/creator/${encodeURIComponent(creatorId)}`,
        typeof cursor === 'string' && cursor !== ''
          ? { headers, params: { cursor } }
          : { headers },
      );

      return { success: true, message: '', page: parsePostsPage(response.data) };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to load posts',
      };
    }
  },
};
